/**
 * Collision Guard & Overlap Prevention Logic.
 * 
 * Implements 2D Separating Axis Theorem (SAT) with touch clearance tolerance (Baying),
 * occupation checks, and free tile search for the Data Hall Floor Plan.
 */

export interface Box2D {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation?: number;
}

interface OBB {
  id: string;
  cx: number;
  cy: number;
  hu: number; // half-width
  hv: number; // half-height
  axes: { x: number; y: number }[];
}

/**
 * Constructs an Oriented Bounding Box (OBB) from Box2D coordinates and rotation in degrees.
 */
export function getOBB(box: Box2D): OBB {
  const rotationDeg = box.rotation || 0;
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;

  // Primary unit axes of the box
  const ax = { x: cos, y: sin };
  const ay = { x: -sin, y: cos };

  return {
    id: box.id,
    cx,
    cy,
    hu: box.w / 2,
    hv: box.h / 2,
    axes: [ax, ay],
  };
}

/**
 * Evaluates whether two boxes intersect using Separating Axis Theorem (SAT).
 * @param a First box
 * @param b Second box
 * @param touchTolerancePx Tolerance (in pixels) to treat touching flush edges as non-colliding (Baying).
 *                          Default is 0.5 px (approx 5 mm in 100 ppm scale).
 */
export function boxesIntersectOBB(
  a: Box2D,
  b: Box2D,
  touchTolerancePx: number = 0.5
): boolean {
  const obbA = getOBB(a);
  const obbB = getOBB(b);

  const dx = obbB.cx - obbA.cx;
  const dy = obbB.cy - obbA.cy;
  const T = { x: dx, y: dy };

  // All candidate axes from both boxes
  const axes = [...obbA.axes, ...obbB.axes];

  for (const axis of axes) {
    // Project vector T between centers onto axis
    const projT = Math.abs(T.x * axis.x + T.y * axis.y);

    // Sum of projected half-sizes onto axis
    const projA =
      obbA.hu * Math.abs(obbA.axes[0].x * axis.x + obbA.axes[0].y * axis.y) +
      obbA.hv * Math.abs(obbA.axes[1].x * axis.x + obbA.axes[1].y * axis.y);

    const projB =
      obbB.hu * Math.abs(obbB.axes[0].x * axis.x + obbB.axes[0].y * axis.y) +
      obbB.hv * Math.abs(obbB.axes[1].x * axis.x + obbB.axes[1].y * axis.y);

    // If projection distance exceeds or equals the sum of half-sizes minus touch tolerance,
    // a separating axis is found, meaning they do not penetrate each other.
    if (projT >= projA + projB - touchTolerancePx) {
      return false;
    }
  }

  // Penetration detected along all axes -> true collision
  return true;
}

/**
 * Checks all entities against each other and returns an array of unique entity IDs that collide.
 */
export function findCollidingEntityIds(
  entities: Box2D[],
  touchTolerancePx: number = 0.5
): string[] {
  const collidingSet = new Set<string>();

  for (let i = 0; i < entities.length; i++) {
    for (let j = i + 1; j < entities.length; j++) {
      if (boxesIntersectOBB(entities[i], entities[j], touchTolerancePx)) {
        collidingSet.add(entities[i].id);
        collidingSet.add(entities[j].id);
      }
    }
  }

  return Array.from(collidingSet);
}

/**
 * Checks if a proposed candidate box intersects with any existing entity (excluding itself).
 */
export function isPositionOccupied(
  candidate: Box2D,
  entities: Box2D[],
  touchTolerancePx: number = 0.5
): boolean {
  for (const entity of entities) {
    if (entity.id === candidate.id) continue;
    if (boxesIntersectOBB(candidate, entity, touchTolerancePx)) {
      return true;
    }
  }
  return false;
}

/**
 * Finds the nearest unoccupied tile position if the initial target tile is occupied.
 * Spirals outwards in integer multiples of `stepPx` (default 60 px = 600 mm raised floor tile).
 */
export function findNearestFreePosition(
  candidate: Box2D,
  entities: Box2D[],
  stepPx: number = 60,
  maxRadiusTiles: number = 5
): { x: number; y: number } {
  if (!isPositionOccupied(candidate, entities)) {
    return { x: candidate.x, y: candidate.y };
  }

  const origX = candidate.x;
  const origY = candidate.y;

  for (let r = 1; r <= maxRadiusTiles; r++) {
    // Collect all candidate offsets in ring of radius r
    const ringOffsets: Array<{ dx: number; dy: number; distSq: number }> = [];
    for (let dx = -r; dx <= r; dx++) {
      for (let dy = -r; dy <= r; dy++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        ringOffsets.push({ dx, dy, distSq: dx * dx + dy * dy });
      }
    }
    // Sort offsets: orthogonal neighbors (distSq = 1 for r=1) evaluated before diagonals (distSq = 2)
    ringOffsets.sort((a, b) => a.distSq - b.distSq);

    for (const { dx, dy } of ringOffsets) {
      const testX = origX + dx * stepPx;
      const testY = origY + dy * stepPx;

      // Skip negative coordinates (outside hall boundaries)
      if (testX < 0 || testY < 0) continue;

      const testBox: Box2D = {
        ...candidate,
        x: testX,
        y: testY,
      };

      if (!isPositionOccupied(testBox, entities)) {
        return { x: testX, y: testY };
      }
    }
  }

  // Fallback to original if no free tile in range
  return { x: origX, y: origY };
}
